import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { ApiService } from './api.service';
import { environment } from '../../../environments/environment';
import { StorageQuotaCheckParams, StorageQuotaCheckResult } from '../models/storage-quota.model';
import { StorageQuotaPurpose } from '../constants/storage-quota.constants';
import { logger } from '../utils/logger.util';
import { joinApiUrl } from '../utils/api-url.util';


export interface UploadUrlRequest {
  folder: string;
  contentType: string;
  fileName?: string;
  existingKey?: string;
  /** Taille du fichier (bytes) — contrôle quota avant presign */
  fileSize?: number;
  idEhpad?: number;
  idResident?: number;
  idResidents?: number[];
  purpose?: StorageQuotaPurpose;
  mediaType?: string;
}

export interface UploadUrlResponse {
  success: boolean;
  data: {
    key: string;
    uploadUrl: string;
    expiresIn: number;
  };
}

export interface DownloadUrlRequest {
  key: string;
}

export interface DownloadUrlResponse {
  success: boolean;
  data: {
    key: string;
    downloadUrl: string;
    expiresIn: number;
  };
}

export interface DocumentKeyRequest {
  ehpadId: number;
  year: number;
  category: string;
  fileName: string;
  fileExtension: string;
  fileSize?: number;
}

export interface DocumentKeyResponse {
  success: boolean;
  data: {
    key: string;
    folder: string;
  };
}

export interface OfficialDocumentKeyRequest {
  year: number;
  fileName: string;
}

export interface OfficialDocumentKeyResponse {
  success: boolean;
  data: {
    key: string;
    year: number;
    fileName: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class StorageService {
  private readonly endpoint = '/api/storage';

  constructor(private apiService: ApiService) {}

  /**
   * Génère une URL présignée pour l'upload d'un fichier
   */
  generateUploadUrl(request: UploadUrlRequest): Observable<UploadUrlResponse> {
    return this.apiService.post<UploadUrlResponse>(`${this.endpoint}/upload-url`, request);
  }

  /** Prévisualisation quota (GET /storage/quota-check). */
  quotaCheck(params: StorageQuotaCheckParams): Observable<{ success: boolean; data: StorageQuotaCheckResult }> {
    const query: Record<string, string> = { fileSize: String(params.fileSize) };
    if (params.purpose) query['purpose'] = params.purpose;
    const ehpadId = params.idEhpad ?? params.ehpadId;
    if (ehpadId != null) query['idEhpad'] = String(ehpadId);
    if (params.idResident != null) query['idResident'] = String(params.idResident);
    if (params.idResidents != null) {
      query['idResidents'] = Array.isArray(params.idResidents)
        ? params.idResidents.join(',')
        : String(params.idResidents);
    }
    if (params.mediaType) query['mediaType'] = params.mediaType;
    if (params.s3Key) query['s3Key'] = params.s3Key;
    if (params.existingKey) query['existingKey'] = params.existingKey;
    return this.apiService.get(`${this.endpoint}/quota-check`, query);
  }

  /**
   * Génère une URL présignée pour le téléchargement / affichage d'un fichier
   */
  generateDownloadUrl(
    key: string,
    options?: { inline?: boolean; purpose?: 'thumbnail' | 'preview'; expiresIn?: number }
  ): Observable<DownloadUrlResponse> {
    const query: Record<string, string> = { key };
    if (options?.inline !== false) {
      query['inline'] = 'true';
    }
    if (options?.purpose) {
      query['purpose'] = options.purpose;
    }
    if (options?.expiresIn != null) {
      query['expiresIn'] = String(options.expiresIn);
    }
    return this.apiService.get<DownloadUrlResponse>(`${this.endpoint}/download-url`, query);
  }

  /**
   * URLs présignées en lot (grilles médias, max 30 clés)
   */
  generateDownloadUrlsBatch(
    keys: string[],
    purpose: 'thumbnail' | 'preview' = 'thumbnail'
  ): Observable<{
    success: boolean;
    data: Record<string, DownloadUrlResponse['data'] | { error: string }>;
  }> {
    return this.apiService.post(`${this.endpoint}/download-urls`, { keys, purpose });
  }

  /**
   * ✅ CORRIGÉ : Générer une clé spécialisée pour un document
   * Maintenant utilise la même logique que les photos de profil
   */
  generateDocumentKey(request: DocumentKeyRequest): Observable<DocumentKeyResponse> {
    logger.debug('🚀 StorageService: Génération clé document:', request);
    
    return this.apiService.post<DocumentKeyResponse>(`${this.endpoint}/keys/document`, request).pipe(
      map(response => {
        if (response.success && response.data?.key) {
          logger.debug('✅ Clé document générée par le backend:', response.data.key);
          logger.debug('🎯 Format attendu: documents/ehpad-{ehpadId}/{year}/{category}/{timestamp}-{uuid}-{nom}.{extension}');
        }
        return response;
      }),
      catchError(error => {
        logger.error('❌ Erreur lors de la génération de la clé document:', error);
        throw error;
      })
    );
  }

  /**
   * Génère une clé pour un document officiel (global)
   */
  generateOfficialDocumentKey(request: OfficialDocumentKeyRequest): Observable<OfficialDocumentKeyResponse> {
    return this.apiService.post<OfficialDocumentKeyResponse>(`${this.endpoint}/keys/document-officiel`, request);
  }

  /**
   * Vérifie si un fichier existe dans le stockage
   */
  checkFileExists(key: string): Observable<{ success: boolean; data: { exists: boolean; key: string } }> {
    return this.apiService.get<{ success: boolean; data: { exists: boolean; key: string } }>(`${this.endpoint}/files/${key}/exists`);
  }

  /**
   * Supprime un fichier du stockage
   */
  deleteFile(key: string): Observable<{ success: boolean; message: string; data: { key: string } }> {
    return this.apiService.delete<{ success: boolean; message: string; data: { key: string } }>(`${this.endpoint}/files/${key}`);
  }

  /**
   * Récupère les métadonnées d'un fichier
   */
  getFileMetadata(key: string): Observable<any> {
    return this.apiService.get(`${this.endpoint}/files/${key}/metadata`);
  }

  /**
   * Récupère la configuration du stockage
   */
  getStorageConfig(): Observable<any> {
    return this.apiService.get(`${this.endpoint}/config`);
  }

  /**
   * Récupère la taille du bucket de stockage
   */
  getBucketSize(): Observable<any> {
    return this.apiService.get(`${this.endpoint}/bucket/size`);
    }

  // ✅ NOUVELLES FONCTIONS POUR LE STREAMING SÉCURISÉ (VISUALISATION UNIQUEMENT)

  /**
   * Récupérer une image en base64 pour l'affichage frontend
   */
  getImageAsBase64(key: string): Observable<{ success: boolean; data: string; contentType: string; contentLength: number }> {
    return this.apiService.get<{ success: boolean; data: string; contentType: string; contentLength: number }>(`${this.endpoint}/files/${encodeURIComponent(key)}/base64`);
  }

  /**
   * ✅ NOUVELLE APPROCHE : Utiliser base64 pour l'affichage (plus fiable)
   */
  getProfilePhotoUrl(key: string): Observable<string> {
    return this.getImageAsBase64(key).pipe(
      map(response => {
        if (response.success && response.data) {
          return response.data; // Retourne directement le data:image/...;base64,...
        } else {
          throw new Error('Impossible de récupérer l\'image en base64');
        }
      }),
      catchError(error => {
        throw error;
      })
    );
  }

  /**
   * Générer l'URL de streaming sécurisée pour la visualisation
   */
  getStreamUrl(key: string): string {
    return joinApiUrl(
      environment.apiUrl,
      `/api/storage/files/${encodeURIComponent(key)}/stream`
    );
  }

  /**
   * Générer l'URL de streaming pour les images (visualisation)
   */
  getImageUrl(key: string): string {
    return this.getStreamUrl(key);
  }

  /**
   * ✅ REMPLACÉ : Utiliser base64 au lieu du streaming pour les photos de profil
   * Cette fonction est maintenant remplacée par getProfilePhotoUrl(key: string): Observable<string>
   */
  // getProfilePhotoUrl(key: string): string {
  //   return this.getStreamUrl(key);
  // }

  /**
   * ✅ ÉTAPE 1 : Générer une clé pour une photo de profil via le backend
   * Format : profiles/utilisateurs/{userId}/{userId}+nom+prenom.{extension}
   */
  generateProfilePhotoKey(userId: number, file: File): Observable<{ success: boolean; data: { key: string } }> {
    logger.debug('🚀 StorageService: ÉTAPE 1 - Génération clé photo profil:', { userId, fileName: file.name });

    // ✅ Appeler le backend pour générer la clé au format id+nom+prenom.extension
    const request = {
      userId: userId,
      fileName: file.name
    };

    logger.debug('📤 Envoi requête au backend:', request);

    return this.apiService.post<{ success: boolean; data: { key: string; format: string; fileName: string } }>(
      `${this.endpoint}/keys/profile-photo`,
      request
    ).pipe(
      map(response => {
        if (response.success && response.data?.key) {
          logger.debug('✅ Clé générée par le backend:', response.data.key);
          logger.debug('🎯 Format attendu:', response.data.format);
          logger.debug('🎯 Format attendu:', response.data.fileName);
          logger.debug('🎯 Format attendu: profiles/utilisateurs/{userId}/{userId}+nom+prenom.{extension}');
        }
        return response;
      }),
      catchError(error => {
        logger.error('❌ Erreur lors de la génération de la clé:', error);
        throw error;
      })
    );
  }

  /**
   * Récupérer un document en base64 pour l'affichage frontend
   */
  getDocumentAsBase64(key: string): Observable<any> {
    return this.apiService.get<any>(`${this.endpoint}/files/${encodeURIComponent(key)}/document-base64`).pipe(
      map(response => {
        if (response.success && response.data) {
          return response;
        } else {
          logger.error('❌ Erreur récupération document base64:', response);
          throw new Error('Impossible de récupérer le document en base64');
        }
      }),
      catchError(error => {
        logger.error('❌ Erreur lors de la récupération document base64:', error);
        throw error;
      })
    );
  }

  /**
   * Récupérer un audio en base64 pour l'affichage frontend
   */
  getAudioAsBase64(key: string): Observable<any> {
    logger.debug('🎯 StorageService: Récupération audio base64, clé:', key);
    return this.apiService.get<any>(`${this.endpoint}/files/${encodeURIComponent(key)}/audio-base64`).pipe(
      map(response => {
        if (response.success && response.data) {
          logger.debug('✅ Audio base64 récupéré avec succès, longueur:', response.contentLength);
          return response;
        } else {
          logger.error('❌ Erreur récupération audio base64:', response);
          throw new Error('Impossible de récupérer l\'audio en base64');
        }
      }),
      catchError(error => {
        logger.error('❌ Erreur lors de la récupération audio base64:', error);
        throw error;
      })
    );
  }

  /**
   * Générer une clé pour une photo
   */
  generatePhotoKey(request: {
    ehpadId: number;
    year: number;
    sousType: string;
    fileName: string;
    fileExtension: string;
    fileSize?: number;
    idResident?: number;
    idResidents?: number[];
  }): Observable<{ success: boolean; data: { key: string } }> {
    logger.debug('🚀 Génération clé photo:', request);
    return this.apiService.post<{ success: boolean; data: { key: string } }>(
      `${this.endpoint}/keys/photo`,
      request
    );
  }

  /**
   * Générer une clé pour une vidéo
   */
  generateVideoKey(request: {
    ehpadId: number;
    year: number;
    sousType: string;
    fileName: string;
    fileExtension: string;
    fileSize?: number;
    idResident?: number;
    idResidents?: number[];
  }): Observable<{ success: boolean; data: { key: string } }> {
    logger.debug('🚀 Génération clé vidéo:', request);
    return this.apiService.post<{ success: boolean; data: { key: string } }>(
      `${this.endpoint}/keys/video`,
      request
    );
  }

  /**
   * Générer une clé pour une animation 3D
   */
  generateAnimation3DKey(request: {
    ehpadId: number;
    year: number;
    sousType: string;
    fileName: string;
    fileExtension: string;
  }): Observable<{ success: boolean; data: { key: string } }> {
    logger.debug('🚀 Génération clé animation 3D:', request);
    return this.apiService.post<{ success: boolean; data: { key: string } }>(
      `${this.endpoint}/keys/animation-3d`,
      request
    );
  }

  /**
   * Générer une clé pour un fichier public
   */
  generatePublicKey(request: {
    category: string;
    fileName: string;
    fileExtension: string;
  }): Observable<{ success: boolean; data: { key: string } }> {
    logger.debug('🚀 Génération clé publique:', request);
    return this.apiService.post<{ success: boolean; data: { key: string } }>(
      `${this.endpoint}/keys/public`,
      request
    );
  }

  /**
   * Générer une clé pour un document RGPD
   */
  generateRgpdDocumentKey(request: {
    ehpadId: number;
    year: number;
    fileName: string;
    fileSize?: number;
  }): Observable<{ success: boolean; data: { key: string; ehpadId: number; year: number; fileName: string } }> {
    logger.debug('🚀 Génération clé document RGPD:', request);
    return this.apiService.post<{ success: boolean; data: { key: string; ehpadId: number; year: number; fileName: string } }>(
      `${this.endpoint}/keys/rgpd-document`,
      request
    );
  }

  /**
   * Clé S3 campagne d'information — quota établissement (préfixe campaigns/ehpad-{id}/).
   */
  generateInformationCampaignKey(request: {
    ehpadId: number;
    year: number;
    fileName: string;
    fileSize?: number;
  }): Observable<{
    success: boolean;
    data: { key: string; ehpadId: number; year: number; fileName: string };
  }> {
    return this.apiService.post<{
      success: boolean;
      data: { key: string; ehpadId: number; year: number; fileName: string };
    }>(`${this.endpoint}/keys/information-campaign`, request);
  }

  /**
   * Générer une clé pour un fichier audio (global, accessible à tous les EHPADs)
   */
  generateAudioKey(request: {
    year: number;
    category: string;
    fileName: string;
  }): Observable<{ 
    success: boolean; 
    data: { 
      key: string;
      year: number;
      category: string;
      fileName: string;
      type: string;
    } 
  }> {
    logger.debug('🚀 Génération clé audio:', request);
    return this.apiService.post<{ 
      success: boolean; 
      data: { 
        key: string;
        year: number;
        category: string;
        fileName: string;
        type: string;
      } 
    }>(`${this.endpoint}/keys/audio`, request);
  }

  /**
   * Générer une clé pour une notice
   */
  generateNoticeKey(request: {
    roleName: string;
    year: number;
    fileType: string;
    fileName: string;
  }): Observable<{ 
    success: boolean; 
    data: { 
      key: string;
      roleName: string;
      year: number;
      fileType: string;
      fileName: string;
    } 
  }> {
    logger.debug('🚀 Génération clé notice:', request);
    return this.apiService.post<{ 
      success: boolean; 
      data: { 
        key: string;
        roleName: string;
        year: number;
        fileType: string;
        fileName: string;
      } 
    }>(`${this.endpoint}/keys/notice`, request);
  }
} 